import "./SiteLayout.scss";
import { SiteLayoutProps } from "/Models/Generated/SiteLayoutProps";
import * as Components from "/App.Components";
import {
  Header,
  Footer,
  MenuOverlay,
  ModalProvider,
  Breadcrumbs,
} from "/App.Components";
import { ReactNode, useEffect, useState } from "react";

export interface SiteLayoutPreviewProps extends SiteLayoutProps {
  mockChildren?: ReactNode;
}

declare global {
  interface Window {
    epi: any;
  }
}

const SiteLayout = (props: SiteLayoutPreviewProps) => {
  const [state, setState] = useState(props);
  const [showOverlayDesktop, setShowOverlayDesktop] = useState(false);
  const [showOverlayMobile, setShowOverlayMobile] = useState(false);

  const {
    mockChildren,
    header,
    breadcrumbs,
    child,
    footer,
    epiFullRefreshPropertyNames,
    updateEndpoint,
  } = state;

  const Comp = (Components as { [index: string]: any })[child?.name ?? ""];

  useEffect(() => {
    if (!updateEndpoint) return;

    const subscribe = (retries: number) => {
      if (retries === 0) {
        console.error("Could not subscribe to Episerver events");
        return;
      }
      if (typeof window.epi === "undefined") {
        console.warn("epi undefined, retrying in 400ms");
        window.setTimeout(() => subscribe(retries - 1), 400);
        return;
      }
      window.epi.subscribe(
        "contentSaved",
        (event: { properties: [{ name: string }]; contentLink: number }) => {
          const fullRefreshNames: string[] = (
            epiFullRefreshPropertyNames ?? []
          ).map((p) => (p ?? "").toLowerCase());
          const updatedNames =
            event.properties?.map((p) => (p.name + "").toLowerCase()) ?? [];

          const overlap = updatedNames.filter((n) =>
            fullRefreshNames.includes(n)
          );
          if ((overlap?.length ?? 0) > 0) {
            // biome-ignore lint/suspicious/noConsole: added for debug
            console.log("Refreshing view in edit mode");
            fetch(updateEndpoint + event.contentLink)
              .then((res) => res.json())
              .then((json) => setState(json));
          }
        }
      );
      // biome-ignore lint/suspicious/noConsole: added for debug
      console.log("Subscribed to the contentSaved event");
    };
    window.setTimeout(() => subscribe(10), 400);
  }, []);

  return (
    <div className="site-layout-norad">
      <ModalProvider>
        <Header
          {...header}
          showOverlayDesktop={setShowOverlayDesktop}
          showOverlayMobile={setShowOverlayMobile}
        />
        <div className="site-layout-norad__below-header">
          <MenuOverlay
            showDesktop={showOverlayDesktop}
            showMobile={showOverlayMobile}
          />
          {breadcrumbs && <Breadcrumbs {...breadcrumbs} />}
          {mockChildren ? (
            <main id="main-content">{mockChildren}</main>
          ) : (
            <main id="main-content">
              {child && <Comp {...child.component} />}
            </main>
          )}
          {footer && <Footer {...footer} />}
        </div>
      </ModalProvider>
    </div>
  );
};

export default SiteLayout;
